// overlayKit/Tag.tsx — universal lower-left tag. The 90% workhorse.
// Production-agnostic: palette via context, resolution-agnostic (positions itself relative to the
// composition's actual width/height via useVideoConfig). Same component for 1280×704, 720×1280,
// or 4K 3840×2160 — px values scale because they're derived from the canvas, not hardcoded.
//
// Numeric count-up locks on the spoken value word's end_s (factory-overlays Rule 2). When
// `valueS` and `endS` are provided, the value animates from 0 → value across [valueS, endS].
import React from "react";
import { useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { clamp, FONT, TS, win } from "./utils";
import { useColor } from "./palette";

export type TagProps = {
  inS: number;
  outS: number;
  label: string;
  sub?: string;
  /** Palette token: "primary"/"secondary"/"neutral" or any custom token in the production's palette. */
  color: string;
  /** Optional numeric count-up. Omit to render a label-only tag. */
  value?: number;
  valueS?: number;
  endS?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  /** Screen position. Default "lower-left" (legacy). "top-left" = step chip. "bottom-center" = unit label. */
  pos?: "lower-left" | "top-left" | "bottom-center";
  /** Optional accent-colored prefix rendered before `label` (e.g. "STEP 1" in yellow, label in white). */
  hi?: string;
};

export const Tag: React.FC<TagProps> = ({
  inS, outS, label, sub, color,
  value, valueS, endS, decimals = 0, prefix = "", suffix = "",
  pos = "lower-left", hi,
}) => {
  const frame = useCurrentFrame();
  const { fps, width } = useVideoConfig();
  const t = frame / fps;
  const hex = useColor(color);

  const op = win(t, inS, outS);
  if (op <= 0.001) return null;

  // Position scales with the composition's actual width — same Tag works at 1280, 720, 3840.
  // 64/70 px insets at 1280 ≈ 5%/10% of width/height — preserve those ratios.
  const leftPx = 22;
  // (Yash 2026-06-07): the danda — gold accent bar above lower-left tags — removed
  // as a universal aesthetic upgrade. White-with-shadow text reads on its own.

  let disp = "";
  if (value != null && valueS != null && endS != null) {
    const v = interpolate(t, [valueS, endS], [0, value], clamp);
    disp = `${prefix}${v.toFixed(decimals)}${suffix}`;
  }

  const container: React.CSSProperties =
    pos === "top-left"
      ? { position: "absolute", left: 22, top: 18, textAlign: "left", alignItems: "flex-start" }
      : pos === "bottom-center"
      ? { position: "absolute", left: 0, right: 0, bottom: 18, textAlign: "center", alignItems: "center", display: "flex", flexDirection: "column" }
      : { position: "absolute", left: leftPx, bottom: 18, textAlign: "left" };

  return (
    <div
      style={{
        ...container,
        opacity: op,
        fontFamily: FONT,
      }}
    >
      {value != null && (
        <div
          style={{
            fontSize: 60,
            fontWeight: 800,
            color: "#ffffff",
            letterSpacing: "-2px",
            lineHeight: 1,
            textShadow: TS,
          }}
        >
          {disp}
        </div>
      )}
      <div
        style={{
          fontSize: 19,
          fontWeight: 700,
          letterSpacing: "0.16em",
          textShadow: TS,
          marginTop: value != null ? 10 : 0,
        }}
      >
        {hi && <span style={{ color: hex }}>{hi} </span>}
        <span style={{ color: hi ? "#ffffff" : hex }}>{label}</span>
      </div>
      {sub && (
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "#d8c4a0",
            letterSpacing: "0.14em",
            textShadow: TS,
            marginTop: 6,
          }}
        >
          {sub}
        </div>
      )}
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
